(function () {
  'use strict';

  const componentName = 'rcc-product-swatch';
  const colorName = 'color';
  let groupsPromise;

  if (customElements.get(componentName)) return;

  function waitForGroups() {
    if (groupsPromise) return groupsPromise;

    groupsPromise = new Promise((resolve) => {
      let attempts = 100;

      const check = () => {
        let groups = [];

        try {
          groups = window.mpg?.getGroups?.(true) || [];
        } catch (_error) {
          groups = [];
        }

        if (groups.length || attempts-- === 0) {
          if (!groups.length) groupsPromise = undefined;
          resolve(groups);
        } else window.setTimeout(check, 100);
      };

      check();
    });

    return groupsPromise;
  }

  function productHandle(product, component) {
    if (product?.handle) return product.handle;

    const url = product?.url || component.closest('.rcc-search__product')?.querySelector('a[href*="/products/"]')?.href;

    try {
      return new URL(url, window.location.origin).pathname.split('/products/')[1]?.split('/')[0] || '';
    } catch (_error) {
      return '';
    }
  }

  function matchingProduct(products, activeProduct, colorOption, value) {
    if (activeProduct && !activeProduct.options?.length) return products.find((product) => product.options?.includes(value.id));

    const colorValueIds = colorOption.values.map((optionValue) => optionValue.id);
    const activeValues = (activeProduct?.options || []).map((optionId) =>
      colorValueIds.includes(optionId) ? value.id : optionId
    );

    return (
      products.find((product) => product.options?.every((optionId) => activeValues.includes(optionId))) ||
      products.find((product) => product.options?.includes(value.id))
    );
  }

  function isActiveProduct(product, id, handle) {
    const groupProductId = String(product.id || '');
    return (
      product.handle === handle ||
      (groupProductId && (id === groupProductId || id.endsWith(`/Product/${groupProductId}`)))
    );
  }

  function makeElement(tagName, className, text) {
    const element = document.createElement(tagName);
    element.className = className;
    if (text) element.textContent = text;
    return element;
  }

  function applySwatchStyle(element, value, product) {
    const image =
      value.style === 'custom_image'
        ? value.image_custom
        : value.style === 'variant_image'
          ? product.variant_image || value.image || product.images?.[0]
          : value.style === 'first_image'
            ? product.images?.[0]
            : '';

    if (value.style === 'color1') element.style.background = value.color1;
    else if (value.style === 'color2') {
      element.style.backgroundImage = `linear-gradient(90deg, ${value.color1} 50%, ${value.color2} 50%)`;
    } else if (image) element.style.backgroundImage = `url(${JSON.stringify(image)})`;
  }

  class RccProductSwatch extends HTMLElement {
    constructor() {
      super();
      this.renderVersion = 0;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePreload = this.handlePreload.bind(this);
    }

    set product(value) {
      const nextKey = `${value?.productId || ''}:${value?.handle || value?.url || ''}`;

      if (nextKey !== this.productKey) this.replaceChildren();
      this.productKey = nextKey;
      this.productData = value;
      this.render();
    }

    get product() {
      return this.productData;
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('pointerover', this.handlePreload);
      this.addEventListener('focusin', this.handlePreload);
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('pointerover', this.handlePreload);
      this.removeEventListener('focusin', this.handlePreload);
    }

    async handleClick(event) {
      const option = event.target.closest('.mpg-swatch__option[data-product-id]');
      if (!option || !this.contains(option)) return;

      event.preventDefault();
      event.stopPropagation();
      if (
        option.getAttribute('aria-pressed') === 'true' ||
        option.getAttribute('aria-disabled') === 'true'
      )
        return;
      if (typeof this.productData?.changeProduct !== 'function') return;

      option.setAttribute('aria-disabled', 'true');

      try {
        await this.productData.changeProduct(option.dataset.productId);
      } catch (error) {
        console.error('Could not change the product card', error);
      } finally {
        option.removeAttribute('aria-disabled');
      }
    }

    handleKeydown(event) {
      const option = event.target.closest('.mpg-swatch__option[data-product-id]');
      if (!option || !this.contains(option) || !['Enter', ' '].includes(event.key)) return;

      event.preventDefault();
      option.click();
    }

    handlePreload(event) {
      const option = event.target.closest('.mpg-swatch__option[data-product-id]');
      if (!option || !this.contains(option) || option.getAttribute('aria-pressed') === 'true') return;
      if (
        event.type === 'pointerover' &&
        event.relatedTarget instanceof Node &&
        option.contains(event.relatedTarget)
      )
        return;
      if (typeof this.productData?.preloadProduct !== 'function') return;

      void this.productData.preloadProduct(option.dataset.productId).catch(() => {});
    }

    async render() {
      const version = ++this.renderVersion;
      if (!this.isConnected || !this.productData) return;

      const id = String(this.productData.productId || '').split('@')[0];
      const handle = productHandle(this.productData, this);
      if (!id && !handle) return;

      const groups = await waitForGroups();
      if (!this.isConnected || version !== this.renderVersion) return;

      const group = groups.find((candidate) =>
        candidate.products?.some((product) => isActiveProduct(product, id, handle))
      );
      const colorOption = group?.options?.find((option) => String(option.name).toLowerCase() === colorName);
      const products = (group?.products || []).filter(
        (product) => product?.handle && (!product.status || product.status === 'ACTIVE')
      );
      const activeProduct = products.find((product) => isActiveProduct(product, id, handle));

      if (!group || !colorOption || !activeProduct) {
        this.replaceChildren();
        return;
      }

      const seenHandles = new Set();
      const swatches = colorOption.values
        .map((value) => ({ value, product: matchingProduct(products, activeProduct, colorOption, value) }))
        .filter(
          ({ product }) =>
            product?.id && !seenHandles.has(product.handle) && seenHandles.add(product.handle)
        );

      if (swatches.length < 2) {
        this.replaceChildren();
        return;
      }

      /* ponytail: Grouptify already owns the swatch data and styling; RCC only needs the matching controls. */
      const swatch = makeElement('div', 'mpg-swatch mpg-card');
      const optionSet = makeElement(
        'div',
        'mpg-swatch-circle mpg-option-set-layout-default mpg-swatch__option-set'
      );
      const selected = swatches.find(({ value }) => activeProduct.options?.includes(value.id));
      const label = makeElement('legend', 'mpg-swatch-circle mpg-swatch__label');
      const nav = makeElement('div', 'mpg-swatch-circle mpg-swatch__nav');

      swatch.setAttribute('group-id', group.id);
      optionSet.setAttribute('os-type', 'group-option');
      optionSet.setAttribute('os-name', colorOption.name);
      label.append(
        makeElement('span', 'mpg-swatch__label-option', colorOption.name),
        makeElement('span', 'mpg-swatch__label-value', selected?.value.name || '')
      );

      swatches.forEach(({ value, product }, index) => {
        const isSelected = product.handle === activeProduct.handle;
        const option = makeElement(
          'span',
          `mpg-swatch-circle mpg-swatch__option mpg-tooltip-container${isSelected ? ' selected' : ''}`
        );
        const color = makeElement('div', '', value.name);

        option.setAttribute('o-name', colorOption.name);
        option.setAttribute('o-value', value.name);
        option.setAttribute('v-id', value.id);
        option.setAttribute('o-id', colorOption.id);
        option.setAttribute('v-index', index);
        option.setAttribute('tabindex', '0');
        option.setAttribute('role', 'button');
        option.setAttribute('aria-label', `Select ${value.name}`);
        option.setAttribute('aria-pressed', String(isSelected));
        option.dataset.productId = product.id;
        if (product.available === false) option.classList.add('disabled');

        applySwatchStyle(color, value, product);
        option.append(makeElement('span', 'mpg-tooltip', value.name), color);
        nav.append(option);
      });

      optionSet.append(label, nav);
      swatch.append(optionSet);
      this.replaceChildren(swatch);
    }
  }

  customElements.define(componentName, RccProductSwatch);
})();
